import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { authenticateUser } from '../utils/mockUsers';
import { BarChart3, Info, Sparkles, User, Users, Copy, Check, Shield, Lock, Mail } from 'lucide-react';
export function LoginPage() {
  const navigate = useNavigate();
  const {
    login
  } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const user = authenticateUser(email, password);
      if (!user) {
        setError('Invalid email or password. Please use one of the demo accounts below.');
        setIsLoading(false);
        return;
      }
      localStorage.setItem('auth_user', JSON.stringify(user));
      await login(email, password);
      // Admin can choose where to go, others go to their role
      if (user.isAdmin) {
        navigate('/creator'); // Default to creator, but they can access both
      } else if (user.role === 'creator') {
        navigate('/creator');
      } else {
        navigate('/manager');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const handleQuickLogin = (email: string, password: string) => {
    setEmail(email);
    setPassword(password);
    setError('');
  };
  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };
  const demoAccounts = [{
    role: 'Admin',
    icon: <Shield className="h-5 w-5" data-id="element-996" />,
    color: 'from-amber-500 to-orange-600',
    accounts: [{
      name: 'Yasmin Sunman',
      email: 'yasmin@creator.com',
      password: 'admin123',
      badge: 'Full Access'
    }]
  }, {
    role: 'Creator',
    icon: <User className="h-5 w-5" data-id="element-997" />,
    color: 'from-primary-500 to-primary-600',
    accounts: [{
      name: 'Tom Weber',
      email: 'tom@creator.com',
      password: 'creator123'
    }, {
      name: 'Lisa Schmidt',
      email: 'lisa@creator.com',
      password: 'creator123'
    }]
  }, {
    role: 'Manager',
    icon: <Users className="h-5 w-5" data-id="element-998" />,
    color: 'from-secondary-500 to-secondary-600',
    accounts: [{
      name: 'Alex Johnson',
      email: 'admin@manager.com',
      password: 'manager123'
    }, {
      name: 'John Smith',
      email: 'john@agency.com',
      password: 'manager123'
    }]
  }];
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4 relative overflow-hidden" data-id="element-999">
      {/* Premium Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1000">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-primary-400/20 to-secondary-400/20 rounded-full blur-3xl animate-pulse" data-id="element-1001"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-br from-secondary-400/20 to-accent-400/20 rounded-full blur-3xl animate-pulse" style={{
        animationDelay: '1s'
      }} data-id="element-1002"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-accent-400/10 to-primary-400/10 rounded-full blur-3xl" data-id="element-1003"></div>
      </div>

      <div className="w-full max-w-6xl relative z-10 animate-fade-in" data-id="element-1004">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1005">
          {/* Left Column - Login Form */}
          <Card variant="premium" className="animate-slide-up backdrop-blur-xl bg-white/95" data-id="element-1006">
            <CardHeader className="text-center space-y-4 pb-8" data-id="element-1007">
              <div className="mx-auto h-16 w-16 bg-gradient-to-br from-primary-600 via-secondary-600 to-accent-600 rounded-2xl flex items-center justify-center text-white mb-4 shadow-2xl shadow-primary-500/40 animate-scale-in" data-id="element-1008">
                <BarChart3 className="h-9 w-9" data-id="element-1009" />
              </div>
              <div data-id="element-1010">
                <CardTitle className="text-4xl font-bold bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 bg-clip-text text-transparent mb-3 tracking-tight" data-id="element-1011">
                  Welcome Back
                </CardTitle>
                <p className="text-base text-slate-600 font-medium" data-id="element-1012">
                  Sign in to your premium account
                </p>
              </div>
            </CardHeader>

            <CardContent className="pt-0" data-id="element-1013">
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1014">
                <div className="space-y-2" data-id="element-1015">
                  <label className="text-sm font-bold text-slate-700 flex items-center" htmlFor="email" data-id="element-1016">
                    <Mail className="h-4 w-4 mr-2 text-primary-600" data-id="element-1017" />
                    Email Address
                  </label>
                  <Input id="email" type="email" placeholder="name@company.com" value={email} onChange={e => setEmail(e.target.value)} className="input-premium h-12 text-base border-2 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20" required data-id="element-1018" />
                </div>

                <div className="space-y-2" data-id="element-1019">
                  <div className="flex items-center justify-between" data-id="element-1020">
                    <label className="text-sm font-bold text-slate-700 flex items-center" htmlFor="password" data-id="element-1021">
                      <Lock className="h-4 w-4 mr-2 text-primary-600" data-id="element-1022" />
                      Password
                    </label>
                    <Link to="#" className="text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors" data-id="element-1023">
                      Forgot password?
                    </Link>
                  </div>
                  <Input id="password" type="password" placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} className="input-premium h-12 text-base border-2 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20" required data-id="element-1024" />
                </div>

                {error && <div className="bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 rounded-xl p-4 animate-slide-down" data-id="element-1025">
                    <p className="text-sm text-red-700 font-semibold" data-id="element-1026">
                      {error}
                    </p>
                  </div>}

                <div className="flex items-center space-x-2" data-id="element-1027">
                  <input type="checkbox" id="remember" className="h-5 w-5 rounded-lg border-2 border-slate-300 text-primary-600 focus:ring-primary-500 focus:ring-offset-0 transition-colors cursor-pointer" data-id="element-1028" />
                  <label htmlFor="remember" className="text-sm text-slate-700 font-semibold cursor-pointer" data-id="element-1029">
                    Remember me for 30 days
                  </label>
                </div>

                <Button type="submit" variant="premium" className="w-full h-14 text-lg font-bold shadow-2xl shadow-primary-500/40 hover:shadow-primary-500/60" disabled={isLoading} data-id="element-1030">
                  {isLoading ? <span className="flex items-center" data-id="element-1031">
                      <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1032">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1033"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1034"></path>
                      </svg>
                      Signing in...
                    </span> : <span className="flex items-center" data-id="element-1035">
                      <Sparkles className="h-6 w-6 mr-2" data-id="element-1036" />
                      Sign In
                    </span>}
                </Button>
              </form>

              <div className="mt-8 text-center" data-id="element-1037">
                <p className="text-base text-slate-600 font-medium" data-id="element-1038">
                  Don't have an account?{' '}
                  <Link to="/signup" className="font-bold text-primary-600 hover:text-primary-700 transition-colors" data-id="element-1039">
                    Create free account
                  </Link>
                </p>
              </div>

              {/* Trust Badges */}
              <div className="mt-8 pt-6 border-t-2 border-slate-100" data-id="element-1040">
                <div className="flex items-center justify-center space-x-8 text-xs text-slate-500" data-id="element-1041">
                  <div className="flex items-center space-x-2" data-id="element-1042">
                    <div className="h-6 w-6 bg-green-100 rounded-full flex items-center justify-center" data-id="element-1043">
                      <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20" data-id="element-1044">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1045" />
                      </svg>
                    </div>
                    <span className="font-bold" data-id="element-1046">256-bit SSL</span>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-1047">
                    <div className="h-6 w-6 bg-green-100 rounded-full flex items-center justify-center" data-id="element-1048">
                      <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20" data-id="element-1049">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1050" />
                      </svg>
                    </div>
                    <span className="font-bold" data-id="element-1051">GDPR Compliant</span>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-1052">
                    <div className="h-6 w-6 bg-green-100 rounded-full flex items-center justify-center" data-id="element-1053">
                      <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20" data-id="element-1054">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1055" />
                      </svg>
                    </div>
                    <span className="font-bold" data-id="element-1056">SOC 2 Certified</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right Column - Demo Accounts */}
          <div className="space-y-6 animate-slide-up" style={{
          animationDelay: '0.1s'
        }} data-id="element-1057">
            <Card variant="premium" className="bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-50 border-2 border-blue-200/60 backdrop-blur-xl" data-id="element-1058">
              <CardHeader data-id="element-1059">
                <div className="flex items-center space-x-3" data-id="element-1060">
                  <div className="h-10 w-10 bg-blue-100 rounded-xl flex items-center justify-center" data-id="element-1061">
                    <Info className="h-6 w-6 text-blue-600" data-id="element-1062" />
                  </div>
                  <div data-id="element-1063">
                    <CardTitle className="text-blue-900 text-lg" data-id="element-1064">
                      Demo Accounts
                    </CardTitle>
                    <p className="text-sm text-blue-700 font-medium mt-1" data-id="element-1065">
                      Click any account to auto-fill credentials
                    </p>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {demoAccounts.map(category => <Card key={category.role} variant="premium" className="backdrop-blur-xl bg-white/95" data-id="element-1066">
                <CardHeader className="pb-4" data-id="element-1067">
                  <div className="flex items-center space-x-3" data-id="element-1068">
                    <div className={`h-12 w-12 bg-gradient-to-br ${category.color} rounded-xl flex items-center justify-center text-white shadow-xl`} data-id="element-1069">
                      {category.icon}
                    </div>
                    <div data-id="element-1070">
                      <h3 className="font-bold text-slate-900 text-base" data-id="element-1071">
                        {category.role} Accounts
                      </h3>
                      <p className="text-xs text-slate-600 font-semibold" data-id="element-1072">
                        {category.accounts.length} demo account
                        {category.accounts.length > 1 ? 's' : ''} available
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3" data-id="element-1073">
                  {category.accounts.map((account, index) => <div key={index} className="group bg-gradient-to-br from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-200 rounded-xl p-4 transition-all cursor-pointer border-2 border-slate-200/60 hover:border-slate-300 hover:shadow-lg" onClick={() => handleQuickLogin(account.email, account.password)} data-id="element-1074">
                      <div className="flex items-center justify-between mb-3" data-id="element-1075">
                        <div className="flex items-center space-x-2" data-id="element-1076">
                          <h4 className="font-bold text-slate-900 text-sm" data-id="element-1077">
                            {account.name}
                          </h4>
                          {account.badge && <span className="px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold rounded-full" data-id="element-1078">
                              {account.badge}
                            </span>}
                        </div>
                        <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity h-8 px-3 font-bold" onClick={e => {
                    e.stopPropagation();
                    handleQuickLogin(account.email, account.password);
                  }} data-id="element-1079">
                          Quick Login
                        </Button>
                      </div>
                      <div className="space-y-2" data-id="element-1080">
                        <div className="flex items-center justify-between text-xs" data-id="element-1081">
                          <span className="text-slate-600 font-bold" data-id="element-1082">
                            Email:
                          </span>
                          <div className="flex items-center space-x-2" data-id="element-1083">
                            <code className="text-slate-800 bg-white px-3 py-1.5 rounded-lg border-2 border-slate-200 font-semibold" data-id="element-1084">
                              {account.email}
                            </code>
                            <button onClick={e => {
                        e.stopPropagation();
                        copyToClipboard(account.email, `${category.role}-${index}-email`);
                      }} className="text-slate-400 hover:text-slate-600 transition-colors p-1 hover:bg-white rounded" data-id="element-1085">
                              {copiedField === `${category.role}-${index}-email` ? <Check className="h-4 w-4 text-green-600" data-id="element-1086" /> : <Copy className="h-4 w-4" data-id="element-1087" />}
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs" data-id="element-1088">
                          <span className="text-slate-600 font-bold" data-id="element-1089">
                            Password:
                          </span>
                          <div className="flex items-center space-x-2" data-id="element-1090">
                            <code className="text-slate-800 bg-white px-3 py-1.5 rounded-lg border-2 border-slate-200 font-semibold" data-id="element-1091">
                              {account.password}
                            </code>
                            <button onClick={e => {
                        e.stopPropagation();
                        copyToClipboard(account.password, `${category.role}-${index}-password`);
                      }} className="text-slate-400 hover:text-slate-600 transition-colors p-1 hover:bg-white rounded" data-id="element-1092">
                              {copiedField === `${category.role}-${index}-password` ? <Check className="h-4 w-4 text-green-600" data-id="element-1093" /> : <Copy className="h-4 w-4" data-id="element-1094" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>)}
                </CardContent>
              </Card>)}
          </div>
        </div>
      </div>
    </div>;
}